import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { auth, db } from '../../firebase/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import GlobalLoader from '../common/GlobalLoader';

interface AlternativeAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AlternativeAuthModal({ isOpen, onClose, onSuccess }: AlternativeAuthModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isReady: googleAuthReady } = useGoogleAuth();
  const googleAuthReadyRef = useRef(googleAuthReady);
  useEffect(() => {
    googleAuthReadyRef.current = googleAuthReady;
  }, [googleAuthReady]);

  const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
    let tId: any;
    const timeoutPromise = new Promise<T>((_, reject) => {
      tId = setTimeout(() => reject(new Error(`Timeout: ${label}`)), ms);
    });
    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      clearTimeout(tId);
    }
  };

  const waitForGoogleAuthReady = async (ms: number) => {
    const end = Date.now() + ms;
    while (Date.now() < end) {
      if (googleAuthReadyRef.current) return;
      await new Promise((r) => setTimeout(r, 150));
    }
  };

  if (loading) {
    return <GlobalLoader message={t('auth.signingIn', { defaultValue: 'Iniciando sesión...' }) as string} />;
  }

  const validateAdminAccess = async (uid: string): Promise<boolean> => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (!userDocSnap.exists()) {
        setError('Usuario no encontrado');
        return false;
      }

      const userData = userDocSnap.data();
      
      // Verificar si es superadmin
      if (userData?.role === 'admin' || userData?.role === 'superadmin') {
        return true;
      }

      // Verificar si es owner o admin de la familia activa
      const activeFamilyId = userData?.activeFamilyId || (userData?.families?.length > 0 ? userData.families[0] : null);
      
      if (activeFamilyId) {
        const familyDocRef = doc(db, 'families', activeFamilyId);
        const familyDocSnap = await getDoc(familyDocRef);
        
        if (familyDocSnap.exists()) {
          const familyData = familyDocSnap.data();
          const isOwner = familyData?.ownerId === uid;
          const isAdmin = Array.isArray(familyData?.admins) && familyData.admins.includes(uid);
          
          if (isOwner || isAdmin) {
            return true;
          }
        }
      }

      setError('Solo administradores pueden salir del modo grupo');
      return false;
    } catch (err) {
      setError('Error al verificar permisos');
      return false;
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!email || !password) {
      setError('Completa email y contraseña');
      return;
    }

    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const isAdmin = await validateAdminAccess(cred.user.uid);
      
      if (isAdmin) {
        onSuccess();
      }
    } catch (err: any) {
      setError('Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);
    
    try {
      const platform = Capacitor.getPlatform();
      const isNative = platform === 'android' || platform === 'ios';
      let uid = '';

      if (isNative) {
        if (platform === 'ios') {
          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
          const result = await FirebaseAuthentication.signInWithGoogle();
          const idToken = String(result?.credential?.idToken || '').trim();
          if (!idToken) {
            throw new Error('No se recibió token de autenticación');
          }

          const credential = GoogleAuthProvider.credential(idToken);
          const cred = await signInWithCredential(auth, credential);
          uid = cred.user.uid;
        } else {
          if (!googleAuthReadyRef.current) {
            await waitForGoogleAuthReady(4000);
          }
          if (!googleAuthReadyRef.current) {
            throw new Error('Google Auth plugin no está inicializado. Espera un momento y vuelve a intentar.');
          }

          const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');

          try {
            await GoogleAuth.signOut();
          } catch {
            // ignore
          }

          let googleUser: any = null;
          try {
            googleUser = await withTimeout(GoogleAuth.signIn(), 30000, 'GoogleAuth.signIn');
          } catch (err: any) {
            const msg = String(err?.message || '');
            if (msg.includes('Timeout: GoogleAuth.signIn')) {
              try {
                await GoogleAuth.signOut();
              } catch {
              }
              await new Promise((r) => setTimeout(r, 250));
              googleUser = await withTimeout(GoogleAuth.signIn(), 30000, 'GoogleAuth.signIn (retry)');
            } else {
              throw err;
            }
          }
          if (!googleUser?.authentication?.idToken) {
            throw new Error('No se recibió token de autenticación');
          }

          const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
          const cred = await withTimeout(signInWithCredential(auth, credential), 30000, 'signInWithCredential');
          uid = cred.user.uid;
        }
      } else {
        // Web: Usar popup
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        const cred = await signInWithPopup(auth, provider);
        uid = cred.user.uid;
      }

      const isAdmin = await validateAdminAccess(uid);
      
      if (isAdmin) {
        onSuccess();
      }
    } catch (err: any) {
      if (err.message !== 'User cancelled') {
        setError(err?.message || 'Error con Google Sign-In');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="relative w-full max-w-md rounded-[24px] shadow-sm max-h-[80vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: '#fff5eb' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-5 rounded-t-[24px]"
          style={{ backgroundColor: '#055590' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <div className="text-2xl">🔐</div>
            </div>
            <h2 className="text-xl font-black text-white">
              Autenticación Administrativa
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-2xl hover:bg-white/20 flex items-center justify-center transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300 text-center">
                {error}
              </p>
            </div>
          )}

          {/* Email/Password Form */}
          <form onSubmit={handleEmailLogin} className="space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 text-sm bg-transparent border-2 rounded-xl border-gray-200 dark:border-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400"
              placeholder="admin@email.com"
              disabled={loading}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 text-sm bg-transparent border-2 rounded-xl border-gray-200 dark:border-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400"
              placeholder="Contraseña"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verificando...' : '📧 Ingresar con Email'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative py-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 text-gray-400 bg-white dark:bg-gray-900">o</span>
            </div>
          </div>

          {/* Google Button */}
          <button
            type="button"
            disabled={loading}
            onClick={handleGoogleLogin}
            className="inline-flex items-center justify-center w-full gap-3 py-4 px-6 text-lg font-medium text-gray-700 transition-colors bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Ingresar con Google
          </button>

          {/* Cancel Button */}
          <button
            onClick={onClose}
            className="w-full py-3 px-4 rounded-xl border-2 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            disabled={loading}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
